import React, { FC, useCallback, useState } from "react";
import clsx from "clsx";
import { ReactComponent as RequiredDocumentsInsideCircle } from "../../../../assets/svg/required-documents-inside-circle.svg";
import { ReactComponent as Branch } from "../../../../assets/svg/branch-location.svg";
import { ReactComponent as HousingType } from "../../../../assets/svg/housing-type.svg";
import { ReactComponent as ArrowRight } from "../../../../assets/svg/arrow-right.svg";
import { ReactComponent as Scholarship } from "../../../../assets/svg/scholarship.svg";
import { ReactComponent as OtherFees } from "../../../../assets/svg/other-fees.svg";
import { ReactComponent as Location } from "../../../../assets/svg/location-map.svg";
import { AdapterLink } from "../../../../components/Adopter";
import { Routes } from "../../../../constants/routes";
import { urlReplacer } from "../../../../utils";
import { ILanguageCourseCardFrontEndModel } from "../../types";
import { ExpandedRequiredDocuments } from "../CardField/components/ExpandedRequiredDocuments";
import { ExpandedBranch } from "../CardField/components/ExpandedBranch";
import { CardFieldType } from "../constants";
import { useProgramCardStyles } from "./useProgramCardStyles";
import { CourseType } from "../../../language-course/types";
import { CardField } from "../../../../components/CardField/CardField";
import { ExpandedHousingTypes } from "../CardField/components/ExpandedHousingTypes";
import Masker from "../../../../components/Masker/Masker";

interface ILanguageCourseCardProps {
  course: ILanguageCourseCardFrontEndModel;
}

export const LanguageCourseCard: FC<ILanguageCourseCardProps> = ({
  course,
}) => {
  const classes = useProgramCardStyles();

  const [expandedCardFieldId, setExpandedCardFieldId] = useState(null);
  const expandHandler = useCallback((id) => {
    setExpandedCardFieldId((prev) => {
      return prev === id ? null : id;
    });
  }, []);

  const closeCard = () => setExpandedCardFieldId(null);

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <img
          alt={"School Logo"}
          src={course.schoolImage}
          className={classes.schoolImage}
          width={60}
          height={60}
        />
        <div className={classes.headerInfoContainer}>
          <div className={classes.headerInfoSubContainer1}>
            <div className={classes.schoolName}>{course.schoolName}</div>
            <div className={classes.majorName}>{course.language}</div>
            <div className={classes.degreeName}>
              Category - <span className={classes.bold}>{course.category}</span>
            </div>
          </div>
          <div className={classes.headerInfoSubContainer2}>
            <div className={classes.headerProgramTypeContainer}>
              <div
                className={clsx(
                  classes.headerProgramType,
                  course.courseType.type === CourseType.ONLINE
                    ? classes.backgroundOnline
                    : classes.backgroundOnCampus,
                )}
              >
                {course.courseType.name}
              </div>
            </div>
            <Masker
              className={classes.tuitionContainer}
              isMasked={course.isMasked}
            >
              {course.hasFeesSupport && (
                <span className={classes.tuition}>
                  {course.priceOtherFeesSumWithCurrency}
                </span>
              )}
              <span className={classes.tuitionWithScholarship}>
                {course.totalPriceWithUnit}
              </span>
            </Masker>
            <div className={classes.durationContainer}>
              {course.courseDuration}
            </div>
          </div>
        </div>
      </div>
      <div className={classes.content}>
        <CardField
          id={CardFieldType.Branch}
          Icon={Branch}
          title={"Branches"}
          PreviewContent={[course.branches[0].name]}
          ExpandedContent={() => <ExpandedBranch branches={course.branches} />}
          onExpand={expandHandler}
          onClickAway={closeCard}
          isExpanded={expandedCardFieldId === CardFieldType.Branch}
        />
        <CardField
          id={CardFieldType.RequiredDocuments}
          Icon={RequiredDocumentsInsideCircle}
          title={"Required Documents"}
          PreviewContent={course.capitalizedRequiredDocuments}
          ExpandedContent={() => (
            <ExpandedRequiredDocuments
              reqDocs={course.capitalizedRequiredDocuments}
            />
          )}
          onExpand={expandHandler}
          onClickAway={closeCard}
          isExpanded={expandedCardFieldId === CardFieldType.RequiredDocuments}
          isMasked={false}
        />
        <CardField
          id={CardFieldType.HousingType}
          Icon={HousingType}
          title={"Housing Types"}
          PreviewContent={course.housingTypeNames}
          ExpandedContent={() => (
            <ExpandedHousingTypes housingTypes={course.housingTypeNames} />
          )}
          onExpand={expandHandler}
          onClickAway={closeCard}
          isExpanded={expandedCardFieldId === CardFieldType.HousingType}
        />
        <CardField
          Icon={OtherFees}
          title={"Other Fees"}
          PreviewContent={[course.otherFeesTotal]}
          isMasked={course.isMasked}
        />
        <CardField
          Icon={Scholarship}
          title={"Discount"}
          PreviewContent={[course.discount]}
          isMasked={course.isMasked}
        />
      </div>
      <div className={classes.footer}>
        <div className={classes.addressContainer}>
          <Location className={classes.locationIcon} />
          <span className={classes.address}>{course.branchesCountry}</span>
        </div>
        <div>
          <AdapterLink
            to={urlReplacer(Routes.VIEW_COURSE, { id: course.id })}
            className={classes.link}
          >
            <span>View More &nbsp;</span>
            <ArrowRight className={classes.arrowRight} />
          </AdapterLink>
        </div>
      </div>
    </div>
  );
};
